import React, { useState, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, IconButton, Dialog, TextField, TextArea, Select, ScrollArea, Separator, Checkbox, Button, Badge } from "@radix-ui/themes"
import {
  UserPlus,
  Users,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  Clock,
  Key,
  Settings,
  Plus,
  X,
  CheckCircle,
  XCircle
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useToast } from "../../hooks/use-toast"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface RestaurantUser {
  _id: string
  username: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  role: string
  isActive: boolean
  isVerified: boolean
  lastLogin?: string
  permissions: {
    canProcessOrders: boolean
    canManageInventory: boolean
    canManageUsers: boolean
    canViewReports: boolean
    canManageTables: boolean
    canManageMenu: boolean
    canProcessRefunds: boolean
    canViewAnalytics: boolean
  }
  createdAt: string
}

interface UserFormData {
  username: string
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber: string
  role: string
  permissions: {
    canProcessOrders: boolean
    canManageInventory: boolean
    canManageUsers: boolean
    canViewReports: boolean
    canManageTables: boolean
    canManageMenu: boolean
    canProcessRefunds: boolean
    canViewAnalytics: boolean
  }
}

const RestaurantUsersPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { toast } = useToast()
  const { t, language, dir } = useLanguage()
  const [users, setUsers] = useState<RestaurantUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [roleFilter, setRoleFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingUser, setEditingUser] = useState<RestaurantUser | null>(null)
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  })
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    role: "cashier",
    permissions: {
      canProcessOrders: true,
      canManageInventory: false,
      canManageUsers: false,
      canViewReports: false,
      canManageTables: false,
      canManageMenu: false,
      canProcessRefunds: false,
      canViewAnalytics: false
    }
  })

  const fetchUsers = useCallback(async () => {
    if (!restaurantId) return

    try {
      setLoading(true)
      const params: any = {}
      if (searchTerm) params.search = searchTerm
      if (roleFilter && roleFilter !== "all") params.role = roleFilter
      if (statusFilter && statusFilter !== "all") params.isActive = statusFilter

      const response = await restaurantPortalAPI.getRestaurantUsers(restaurantId, params)
      setUsers(response.data.users)
    } catch (error) {
      console.error("Error fetching users:", error)
      toast({
        title: t("error"),
        description: t("failedToLoadStaff"),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [restaurantId, searchTerm, roleFilter, statusFilter, toast])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    try {
      await restaurantPortalAPI.toggleUserStatus(userId, { isActive })
      toast({
        title: t("success"),
        description: isActive ? t("staffMemberActivated") : t("staffMemberDeactivated"),
      })
      fetchUsers()
    } catch (error) {
      toast({
        title: t("error"),
        description: t("failedToUpdateStaffStatus"),
        variant: "destructive",
      })
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm(t("areYouSureDeleteStaffMember"))) {
      try {
        await restaurantPortalAPI.deleteRestaurantUser(userId)
        toast({
          title: t("success"),
          description: t("staffMemberDeleted"),
        })
        fetchUsers()
      } catch (error) {
        toast({
          title: t("error"),
          description: t("failedToDeleteStaffMember"),
          variant: "destructive",
        })
      }
    }
  }

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingUser) {
        // Update existing user
        await restaurantPortalAPI.updateRestaurantUser(editingUser._id, formData)
        toast({
          title: t("success"),
          description: t("staffMemberUpdated"),
        })
      } else {
        // Create new user
        await restaurantPortalAPI.createRestaurantUser({ ...formData, restaurantId })
        toast({
          title: t("success"),
          description: t("staffMemberCreated"),
        })
      }
      setShowAddModal(false)
      setEditingUser(null)
      resetForm()
      fetchUsers()
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error?.response?.data?.message || (editingUser ? t("failedToUpdateStaffMember") : t("failedToCreateStaffMember")),
        variant: "destructive",
      })
    }
  }

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      role: "cashier",
      permissions: {
        canProcessOrders: true,
        canManageInventory: false,
        canManageUsers: false,
        canViewReports: false,
        canManageTables: false,
        canManageMenu: false,
        canProcessRefunds: false,
        canViewAnalytics: false
      }
    })
    setPasswordData({ newPassword: "", confirmPassword: "" })
    setShowPasswordChange(false)
  }

  const handleEditUser = (user: RestaurantUser) => {
    setEditingUser(user)
    setFormData({
      username: user.username,
      email: user.email,
      password: "", // Don't populate password for edits
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      role: user.role,
      permissions: { ...user.permissions }
    })
    setPasswordData({ newPassword: "", confirmPassword: "" })
    setShowPasswordChange(false)
    setShowAddModal(true)
  }

  const handleChangePassword = async () => {
    if (!editingUser) return

    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: t("error"),
        description: t("passwordRequired"),
        variant: "destructive",
      })
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: t("error"),
        description: t("passwordMinLength"),
        variant: "destructive",
      })
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: t("error"),
        description: t("passwordsDoNotMatch"),
        variant: "destructive",
      })
      return
    }

    try {
      await restaurantPortalAPI.updateUserPassword(editingUser._id, {
        newPassword: passwordData.newPassword
      })
      toast({
        title: t("success"),
        description: t("passwordUpdatedSuccessfully"),
      })
      setPasswordData({ newPassword: "", confirmPassword: "" })
      setShowPasswordChange(false)
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error?.response?.data?.message || error?.userMessage || t("failedToUpdatePassword"),
        variant: "destructive",
      })
    }
  }

  const getRoleBadgeVariant = (role: string): "solid" | "soft" => {
    switch (role) {
      case "admin":
        return "solid"
      case "manager":
        return "solid"
      case "cashier":
        return "soft"
      case "waiter":
        return "soft"
      case "kitchen_staff":
        return "soft"
      default:
        return "soft"
    }
  }

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case "admin":
        return t("administrator")
      case "manager":
        return t("manager")
      case "cashier":
        return t("cashier")
      case "waiter":
        return t("waiter")
      case "kitchen_staff":
        return t("kitchenStaff")
      default:
        return role.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getPermissionCount = (permissions: any) => {
    return Object.values(permissions).filter(Boolean).length
  }

  const getFilteredUsers = () => {
    let filtered = users

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter(user => user.role === roleFilter)
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(user => user.isActive === (statusFilter === "true"))
    }

    return filtered
  }

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingStaffData")} />
  }

  const filteredUsers = getFilteredUsers()
  const activeUsers = users.filter(u => u.isActive).length
  const managers = users.filter(u => u.role === "manager" || u.role === "admin").length
  const inactiveUsers = users.filter(u => !u.isActive).length

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("staffManagement")}
        subtitle={t("manageRestaurantStaffMembers")}
        action={
          <Button onClick={() => setShowAddModal(true)} size="3" variant="solid">
            <UserPlus size={16} />
            {t("addStaffMember")}
          </Button>
        }
      />

      {/* Statistics */}
      <Grid columns={{ initial: "1", md: "4" }} gap="4">
        <Card>
          <Flex align="center" gap="4" p="4">
            <Box style={{ padding: "8px", borderRadius: "var(--radius-2)", backgroundColor: "var(--blue-4)" }}>
              <Users size={24} style={{ color: "var(--blue-9)" }} />
            </Box>
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="2" weight="medium" color="gray">{t("totalStaff")}</Text>
              <Text size="6" weight="bold">{users.length}</Text>
              <Text size="1" color="gray">{t("allTeamMembers")}</Text>
            </Flex>
          </Flex>
        </Card>
        <Card>
          <Flex align="center" gap="4" p="4">
            <Box style={{ padding: "8px", borderRadius: "var(--radius-2)", backgroundColor: "var(--green-4)" }}>
              <UserCheck size={24} style={{ color: "var(--green-9)" }} />
            </Box>
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="2" weight="medium" color="gray">{t("activeStaff")}</Text>
              <Text size="6" weight="bold">{activeUsers}</Text>
              <Text size="1" color="gray">{t("currentlyActive")}</Text>
            </Flex>
          </Flex>
        </Card>
        <Card>
          <Flex align="center" gap="4" p="4">
            <Box style={{ padding: "8px", borderRadius: "var(--radius-2)", backgroundColor: "var(--purple-4)" }}>
              <Shield size={24} style={{ color: "var(--purple-9)" }} />
            </Box>
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="2" weight="medium" color="gray">{t("managers")}</Text>
              <Text size="6" weight="bold">{managers}</Text>
              <Text size="1" color="gray">{t("adminAndManagerRoles")}</Text>
            </Flex>
          </Flex>
        </Card>
        <Card>
          <Flex align="center" gap="4" p="4">
            <Box style={{ padding: "8px", borderRadius: "var(--radius-2)", backgroundColor: "var(--orange-4)" }}>
              <UserX size={24} style={{ color: "var(--orange-9)" }} />
            </Box>
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="2" weight="medium" color="gray">{t("inactive")}</Text>
              <Text size="6" weight="bold">{inactiveUsers}</Text>
              <Text size="1" color="gray">{t("deactivatedAccounts")}</Text>
            </Flex>
          </Flex>
        </Card>
      </Grid>

      {/* Filters */}
      <DataCard
        title={t("filtersAndSearch")}
        subtitle={t("filterAndSearchStaffMembers")}
      >
        <Grid columns={{ initial: "1", md: "4" }} gap="4">
          <TextField.Root
            placeholder={t("searchStaffMembers")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          >
            <TextField.Slot>
              <Search size={16} />
            </TextField.Slot>
          </TextField.Root>

          <Select.Root value={roleFilter} onValueChange={setRoleFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("filterByRole")} />
            <Select.Content>
              <Select.Item value="all">{t("allRoles")}</Select.Item>
              <Select.Item value="admin">{t("administrator")}</Select.Item>
              <Select.Item value="manager">{t("manager")}</Select.Item>
              <Select.Item value="cashier">{t("cashier")}</Select.Item>
              <Select.Item value="waiter">{t("waiter")}</Select.Item>
              <Select.Item value="kitchen_staff">{t("kitchenStaff")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Select.Root value={statusFilter} onValueChange={setStatusFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("filterByStatus")} />
            <Select.Content>
              <Select.Item value="all">{t("allStatus")}</Select.Item>
              <Select.Item value="true">{t("active")}</Select.Item>
              <Select.Item value="false">{t("inactive")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Button onClick={fetchUsers} variant="outline" size="3">
            <Filter size={16} />
            {t("applyFilters")}
          </Button>
        </Grid>
      </DataCard>

      {/* Staff Members Grid */}
      <DataCard
        title={t("staffMembers")}
        subtitle={`${filteredUsers.length} ${t("ofStaffMembers").replace("{total}", users.length.toString())}`}
        isEmpty={filteredUsers.length === 0}
        emptyMessage={searchTerm || (roleFilter !== "all") || (statusFilter !== "all") ? t("tryAdjustingYourFilters") : t("getStartedByAddingFirstStaffMember")}
      >
        <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="4">
          {filteredUsers.map((user) => (
            <Card key={user._id} variant="surface">
              <Flex direction="column" gap="4" p="4">
                <Flex justify="between" align="center">
                  <Flex align="center" gap="3">
                    <Box
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--gray-3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Users size={24} style={{ color: 'var(--gray-11)' }} />
                    </Box>
                    <Flex direction="column" gap="1">
                      <Text size="3" weight="bold">
                        {user.firstName} {user.lastName}
                      </Text>
                      <Text size="1" color="gray">@{user.username}</Text>
                    </Flex>
                  </Flex>
                  <Flex gap="2">
                    <IconButton
                      variant="ghost"
                      size="2"
                      onClick={() => handleEditUser(user)}
                    >
                      <Edit size={16} />
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      size="2"
                      onClick={() => handleToggleStatus(user._id, !user.isActive)}
                    >
                      {user.isActive ? (
                        <CheckCircle size={16} style={{ color: 'var(--green-9)' }} />
                      ) : (
                        <XCircle size={16} style={{ color: 'var(--red-9)' }} />
                      )}
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      size="2"
                      onClick={() => handleDeleteUser(user._id)}
                    >
                      <Trash2 size={16} style={{ color: 'var(--red-9)' }} />
                    </IconButton>
                  </Flex>
                </Flex>

                <Flex direction="column" gap="2">
                  <Flex align="center" gap="2">
                    <Mail size={16} style={{ color: 'var(--gray-9)' }} />
                    <Text size="2" color="gray">{user.email}</Text>
                  </Flex>
                  <Flex align="center" gap="2">
                    <Phone size={16} style={{ color: 'var(--gray-9)' }} />
                    <Text size="2" color="gray">{user.phoneNumber}</Text>
                  </Flex>
                  <Flex align="center" gap="2">
                    <Calendar size={16} style={{ color: 'var(--gray-9)' }} />
                    <Text size="2" color="gray">{t("joined")} {formatDate(user.createdAt)}</Text>
                  </Flex>
                  {user.lastLogin && (
                    <Flex align="center" gap="2">
                      <Clock size={16} style={{ color: 'var(--gray-9)' }} />
                      <Text size="2" color="gray">{t("lastLogin")} {formatDate(user.lastLogin)}</Text>
                    </Flex>
                  )}
                </Flex>

                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Badge
                      variant={getRoleBadgeVariant(user.role)}
                      color={user.role === "admin" ? "red" : user.role === "manager" ? "blue" : "gray"}
                    >
                      {getRoleDisplayName(user.role)}
                    </Badge>
                    <Badge
                      variant={user.isActive ? "solid" : "soft"}
                      color={user.isActive ? "green" : "red"}
                    >
                      {user.isActive ? t("active") : t("inactive")}
                    </Badge>
                  </Flex>

                  <Separator />

                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2" weight="medium">{t("permissions")}</Text>
                      <Badge variant="soft" color="gray">
                        {getPermissionCount(user.permissions)} {t("activePermissions")}
                      </Badge>
                    </Flex>
                    <Grid columns="2" gap="2">
                      {Object.entries(user.permissions).map(([key, value]) => (
                        <Flex key={key} align="center" gap="1">
                          <Shield size={12} style={{ color: value ? 'var(--green-9)' : 'var(--gray-6)' }} />
                          <Text size="1" color={value ? undefined : "gray"}>
                            {t(key as any) || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                        </Flex>
                      ))}
                    </Grid>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>
      </DataCard>

      {/* Add/Edit Staff Modal */}
      <Dialog.Root
        open={showAddModal}
        onOpenChange={(open) => {
          setShowAddModal(open)
          if (!open) {
            setEditingUser(null)
            setShowPasswordChange(false)
            resetForm()
          }
        }}
      >
        <Dialog.Content style={{ maxWidth: 720, maxHeight: "90vh" }}>
          <ScrollArea>
            <Flex direction="column" gap="4">
              <Dialog.Title>
                {editingUser ? t("editStaffMember") : t("addNewStaffMember")}
              </Dialog.Title>

              <form onSubmit={handleSubmitUser}>
                <Flex direction="column" gap="4">
                  <Grid columns={{ initial: "1", md: "2" }} gap="4">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("firstName")}</Text>
                      <TextField.Root
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder={t("firstName")}
                        required
                      />
                    </Flex>
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("lastName")}</Text>
                      <TextField.Root
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder={t("lastName")}
                        required
                      />
                    </Flex>
                  </Grid>

                  <Grid columns={{ initial: "1", md: "2" }} gap="4">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("username")}</Text>
                      <TextField.Root
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder={t("username")}
                        required
                      />
                    </Flex>
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("emailAddress")}</Text>
                      <TextField.Root
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t("emailAddress")}
                        required
                      />
                    </Flex>
                  </Grid>

                  <Grid columns={{ initial: "1", md: "2" }} gap="4">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("phoneNumber")}</Text>
                      <TextField.Root
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        placeholder={t("phoneNumber")}
                        required
                      />
                    </Flex>
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("role")}</Text>
                      <Select.Root
                        value={formData.role}
                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                      >
                        <Select.Trigger />
                        <Select.Content>
                          <Select.Item value="cashier">{t("cashier")}</Select.Item>
                          <Select.Item value="waiter">{t("waiter")}</Select.Item>
                          <Select.Item value="kitchen_staff">{t("kitchenStaff")}</Select.Item>
                          <Select.Item value="manager">{t("manager")}</Select.Item>
                          <Select.Item value="admin">{t("administrator")}</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </Grid>

                  {!editingUser && (
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">{t("password")}</Text>
                      <TextField.Root
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={t("password")}
                        required
                      />
                    </Flex>
                  )}

                  {editingUser && (
                    <Flex direction="column" gap="3">
                      <Separator />
                      <Flex justify="between" align="center">
                        <Text size="2" weight="medium">{t("changePassword")}</Text>
                        <Button
                          type="button"
                          variant={showPasswordChange ? "outline" : "ghost"}
                          size="2"
                          onClick={() => setShowPasswordChange(!showPasswordChange)}
                        >
                          {showPasswordChange ? t("cancel") : t("changePassword")}
                        </Button>
                      </Flex>
                      {showPasswordChange && (
                        <Flex direction="column" gap="3">
                          <Flex direction="column" gap="1">
                            <Text size="2" weight="medium">{t("newPassword")}</Text>
                            <TextField.Root
                              type="password"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                              placeholder={t("newPassword")}
                            />
                          </Flex>
                          <Flex direction="column" gap="1">
                            <Text size="2" weight="medium">{t("confirmPassword")}</Text>
                            <TextField.Root
                              type="password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                              placeholder={t("confirmPassword")}
                            />
                          </Flex>
                          <Button
                            type="button"
                            variant="solid"
                            size="3"
                            onClick={handleChangePassword}
                          >
                            {t("updatePassword")}
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                  )}

                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">{t("permissions")}</Text>
                    <Grid columns="2" gap="3">
                      {Object.entries(formData.permissions).map(([key, value]) => (
                        <Flex key={key} align="center" gap="2">
                          <Checkbox
                            id={key}
                            checked={value}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              permissions: {
                                ...formData.permissions,
                                [key]: checked
                              }
                            })}
                          />
                          <Text as="label" htmlFor={key} size="2" style={{ cursor: 'pointer' }}>
                            {t(key as any) || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                        </Flex>
                      ))}
                    </Grid>
                  </Flex>

                  <Flex justify="end" gap="3" pt="4">
                    <Dialog.Close>
                      <Button
                        type="button"
                        variant="outline"
                        size="3"
                      >
                        {t("cancel")}
                      </Button>
                    </Dialog.Close>
                    <Button
                      type="submit"
                      variant="solid"
                      size="3"
                    >
                      {editingUser ? t("updateStaffMember") : t("addStaffMember")}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </ScrollArea>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  )
}

export default RestaurantUsersPage 